import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Modal,
  Form,
  Switch,
  Input,
  Button,
  Space,
  Divider,
  Typography,
  Card,
  Tag,
  Select,
  Radio,
  TreeSelect,
  message,
  Row,
  Col,
  Alert,
  Spin,
  Checkbox,
} from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { useDeviceConstants } from '../hooks/useDeviceConstantsStore';
import { useBotTypeOptions } from '../hooks/useDeviceConstants';
import { useAuthStore } from '../stores/authStore';

const { Text, Title } = Typography;
const { TextArea } = Input;
const { Option } = Select;
const { TreeNode } = TreeSelect;

const networkSignaturePresets = [
  { value: 'AS32934', label: 'Meta / Facebook 审核节点' },
  { value: 'AS63293', label: 'Meta Platforms 运营网络' },
  { value: 'AS15169', label: 'Google 全球网络' },
  { value: 'AS396982', label: 'TikTok / ByteDance 北美节点' },
  { value: 'AS138699', label: 'TikTok Pte. Ltd. 亚太节点' },
  { value: 'AS137753', label: 'ByteDance 数据网络' },
  { value: 'AS55962', label: 'Kuaishou / Kwai 广告网络' },
  { value: 'KUAISHOU-KJ-NET', label: 'Kuaishou / Kwai 运营网络' },
];

const ToggleSwitch: React.FC<{
  value: 'allow' | 'block';
  onChange: (value: 'allow' | 'block') => void;
  allowLabel?: string;
  blockLabel?: string;
  disabled?: boolean;
}> = ({ value, onChange, allowLabel = '允许', blockLabel = '排除', disabled }) => {
  const renderButton = (label: string, target: 'allow' | 'block') => {
    const active = value === target;
    return (
      <button
        type="button"
        onClick={() => !disabled && onChange(target)}
        disabled={disabled}
        style={{
          border: 'none',
          outline: 'none',
          cursor: disabled ? 'not-allowed' : 'pointer',
          padding: '6px 18px',
          borderRadius: 12,
          fontWeight: 600,
          fontSize: 14,
          color: active ? '#FFFFFF' : 'var(--color-text-secondary)',
          background: active
            ? 'linear-gradient(135deg, rgba(76, 110, 255, 1) 0%, rgba(108, 75, 255, 1) 100%)'
            : 'transparent',
          boxShadow: active ? '0 8px 16px rgba(80, 102, 255, 0.25)' : 'none',
          transition: 'all 0.2s ease',
          minWidth: 72,
        }}
      >
        {label}
      </button>
    );
  };

  return (
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        borderRadius: 14,
        padding: 4,
        gap: 8,
        background: '#F4F5FB',
      }}
    >
      {renderButton(allowLabel, 'allow')}
      {renderButton(blockLabel, 'block')}
    </div>
  );
};

type DeviceMode = 'allow' | 'block';

const resolveDeviceMode = (
  allowList: string[] | undefined,
  blockList: string[] | undefined,
  fallback: DeviceMode,
): DeviceMode => {
  if (blockList && blockList.length > 0) {
    return 'block';
  }
  if (allowList && allowList.length > 0) {
    return 'allow';
  }
  return fallback;
};

interface DeviceConfigItemProps {
  label: string;
  options: Array<{ value: string; label: string }>;
  allowList: string[];
  blockList: string[];
  placeholder: string;
  showSearch?: boolean;
  readonly?: boolean;
  mode: DeviceMode;
  isTagInput?: boolean;
  isGroupedOsVersion?: boolean;
  isGroupedDeviceModel?: boolean;
  isGroupedOS?: boolean;
  iosVersionOptions?: Array<{ value: string; label: string }>;
  androidVersionOptions?: Array<{ value: string; label: string }>;
  deviceModelsByBrand?: Record<string, Record<string, string>>;
  onModeChange: (mode: DeviceMode, items: string[]) => void;
  onItemsChange: (mode: DeviceMode, items: string[]) => void;
}

const DeviceConfigItem: React.FC<DeviceConfigItemProps> = React.memo(({
  label,
  options,
  allowList,
  blockList,
  placeholder,
  showSearch = false,
  readonly = false,
  mode,
  isTagInput = false,
  isGroupedOsVersion = false,
  isGroupedDeviceModel = false,
  isGroupedOS = false,
  iosVersionOptions = [],
  androidVersionOptions = [],
  deviceModelsByBrand = {},
  onModeChange,
  onItemsChange,
}) => {
  const [selectedItems, setSelectedItems] = useState<string[]>(() => [...allowList, ...blockList]);

  useEffect(() => {
    setSelectedItems([...allowList, ...blockList]);
  }, [allowList, blockList]);

  const handleModeChange = (nextMode: DeviceMode) => {
    if (nextMode === mode) return;
    onModeChange(nextMode, selectedItems);
  };

  const handleItemChange = (items: string[]) => {
    setSelectedItems(items);
    onItemsChange(mode, items);
  };

  const renderTagText = (value: string) => options.find((opt) => opt.value === value)?.label || value;

  // 分组处理：iOS 和 Android 版本分开展示
  const iosItems = selectedItems.filter(item => item.toLowerCase().includes('ios'));
  const androidItems = selectedItems.filter(item => item.toLowerCase().includes('android'));
  const otherItems = selectedItems.filter(item => 
    !item.toLowerCase().includes('ios') && !item.toLowerCase().includes('android')
  );

  const handleGroupedItemChange = (group: 'ios' | 'android', newGroupItems: string[]) => {
    let newItems: string[];
    if (group === 'ios') {
      newItems = [...newGroupItems, ...androidItems, ...otherItems];
    } else {
      newItems = [...iosItems, ...newGroupItems, ...otherItems];
    }
    setSelectedItems(newItems);
    onItemsChange(mode, newItems);
  };

  const handleRemoveItem = (itemToRemove: string) => {
    const newItems = selectedItems.filter((value) => value !== itemToRemove);
    handleItemChange(newItems);
  };

  // 分组展示的操作系统 UI（手机/平板/桌面/TV/手表）
  if (isGroupedOS) {
    // 操作系统分组定义（TV/手表系统归入"其他"）
    const osGroups = {
      mobile: {
        label: '📱 手机系统',
        values: ['iOS', 'Android', 'HarmonyOS', 'KaiOS', 'Fire OS', 'Windows Phone', 'Windows Mobile', 'BlackBerry', 'Symbian'],
      },
      tablet: {
        label: '📟 平板系统',
        values: ['iPadOS', 'BlackBerry Tablet OS'],
      },
      desktop: {
        label: '🖥️ 桌面系统',
        values: ['Windows', 'Mac OS X', 'Linux', 'Ubuntu', 'Fedora', 'CentOS', 'Debian', 'Chrome OS'],
      },
    };

    // 收集所有已分组的系统
    const allGroupedValues = Object.values(osGroups).flatMap(g => g.values);
    
    // 计算各分组的选项和已选项
    const getGroupOptions = (groupKey: keyof typeof osGroups) => 
      options.filter(opt => osGroups[groupKey].values.includes(opt.value));
    const getGroupItems = (groupKey: keyof typeof osGroups) => 
      selectedItems.filter(item => osGroups[groupKey].values.includes(item));
    const otherOSOptions = options.filter(opt => !allGroupedValues.includes(opt.value));
    const otherOSItems = selectedItems.filter(item => !allGroupedValues.includes(item));

    const handleGroupedOSChange = (groupKey: string, newGroupItems: string[]) => {
      // 保留其他分组的选项，只更新当前分组
      const otherItems = selectedItems.filter(item => {
        if (groupKey === 'other') {
          return allGroupedValues.includes(item);
        }
        return !osGroups[groupKey as keyof typeof osGroups]?.values.includes(item);
      });
      const newItems = [...otherItems, ...newGroupItems];
      setSelectedItems(newItems);
      onItemsChange(mode, newItems);
    };

    return (
      <div>
        <Title level={5}>{label}</Title>

        <div style={{ marginBottom: 16 }}>
          <Space align="center">
            <Text strong>拦截模式：</Text>
            <ToggleSwitch value={mode} onChange={handleModeChange} disabled={readonly} />
          </Space>
        </div>

        <div>
          <Space align="center" style={{ marginBottom: 8 }}>
            <Text strong>{mode === 'allow' ? `允许的${label}：` : `排除的${label}：`}</Text>
          </Space>

          <Card size="small">
            {/* 各分组 */}
            {(Object.keys(osGroups) as Array<keyof typeof osGroups>).map((groupKey) => {
              const groupOptions = getGroupOptions(groupKey);
              if (groupOptions.length === 0) return null;
              return (
                <div key={groupKey} style={{ marginBottom: 16 }}>
                  <Text strong style={{ display: 'block', marginBottom: 8 }}>{osGroups[groupKey].label}</Text>
                  <Checkbox.Group
                    value={getGroupItems(groupKey)}
                    onChange={(checkedValues) => handleGroupedOSChange(groupKey, checkedValues as string[])}
                    disabled={readonly}
                    style={{ width: '100%' }}
                  >
                    <Space wrap>
                      {groupOptions.map((option) => (
                        <Checkbox key={option.value} value={option.value}>
                          {option.label}
                        </Checkbox>
                      ))}
                    </Space>
                  </Checkbox.Group>
                </div>
              );
            })}

            {/* 其他系统（如有） */}
            {otherOSOptions.length > 0 && (
              <div style={{ marginTop: 16 }}>
                <Text strong style={{ display: 'block', marginBottom: 8 }}>🔧 其他系统</Text>
                <Checkbox.Group
                  value={otherOSItems}
                  onChange={(checkedValues) => handleGroupedOSChange('other', checkedValues as string[])}
                  disabled={readonly}
                  style={{ width: '100%' }}
                >
                  <Space wrap>
                    {otherOSOptions.map((option) => (
                      <Checkbox key={option.value} value={option.value}>
                        {option.label}
                      </Checkbox>
                    ))}
                  </Space>
                </Checkbox.Group>
              </div>
            )}
          </Card>
        </div>
      </div>
    );
  }

  // 分组展示的系统版本 UI
  if (isGroupedOsVersion) {
    return (
      <div>
        <Title level={5}>{label}</Title>

        <div style={{ marginBottom: 16 }}>
          <Space align="center">
            <Text strong>拦截模式：</Text>
            <ToggleSwitch value={mode} onChange={handleModeChange} disabled={readonly} />
          </Space>
        </div>

        <div>
          <Space align="center" style={{ marginBottom: 8 }}>
            <Text strong>{mode === 'allow' ? `允许的${label}：` : `排除的${label}：`}</Text>
          </Space>

          <Card size="small">
            {/* iOS 版本组 */}
            <div style={{ marginBottom: 16 }}>
              <Text strong style={{ display: 'block', marginBottom: 8 }}>🍎 iOS 版本</Text>
              <Select
                mode="tags"
                placeholder="选择或输入 iOS 版本"
                style={{ width: '100%' }}
                value={iosItems}
                onChange={(items) => handleGroupedItemChange('ios', items)}
                allowClear
                disabled={readonly}
                tokenSeparators={[',']}
                showSearch
                optionFilterProp="children"
              >
                {iosVersionOptions.map((option) => (
                  <Option key={option.value} value={option.value}>
                    {option.label}
                  </Option>
                ))}
              </Select>
              <div style={{ marginTop: 8 }}>
                {iosItems.map((item) => (
                  <Tag
                    key={item}
                    closable={!readonly}
                    onClose={() => handleRemoveItem(item)}
                    color={mode === 'allow' ? 'green' : 'red'}
                    style={{ marginBottom: 4 }}
                  >
                    {item}
                  </Tag>
                ))}
              </div>
            </div>

            {/* Android 版本组 */}
            <div>
              <Text strong style={{ display: 'block', marginBottom: 8 }}>🤖 Android 版本</Text>
              <Select
                mode="tags"
                placeholder="选择或输入 Android 版本"
                style={{ width: '100%' }}
                value={androidItems}
                onChange={(items) => handleGroupedItemChange('android', items)}
                allowClear
                disabled={readonly}
                tokenSeparators={[',']}
                showSearch
                optionFilterProp="children"
              >
                {androidVersionOptions.map((option) => (
                  <Option key={option.value} value={option.value}>
                    {option.label}
                  </Option>
                ))}
              </Select>
              <div style={{ marginTop: 8 }}>
                {androidItems.map((item) => (
                  <Tag
                    key={item}
                    closable={!readonly}
                    onClose={() => handleRemoveItem(item)}
                    color={mode === 'allow' ? 'green' : 'red'}
                    style={{ marginBottom: 4 }}
                  >
                    {item}
                  </Tag>
                ))}
              </div>
            </div>

            {/* 其他版本（如有） */}
            {otherItems.length > 0 && (
              <div style={{ marginTop: 16 }}>
                <Text strong style={{ display: 'block', marginBottom: 8 }}>其他版本</Text>
                <div>
                  {otherItems.map((item) => (
                    <Tag
                      key={item}
                      closable={!readonly}
                      onClose={() => handleRemoveItem(item)}
                      color={mode === 'allow' ? 'green' : 'red'}
                      style={{ marginBottom: 4 }}
                    >
                      {item}
                    </Tag>
                  ))}
                </div>
              </div>
            )}
          </Card>
        </div>
      </div>
    );
  }

  // 分组展示的设备型号 UI（横向布局）
  if (isGroupedDeviceModel && Object.keys(deviceModelsByBrand).length > 0) {
    const brandGroups = Object.keys(deviceModelsByBrand);

    return (
      <div>
        <Title level={5}>{label}</Title>

        <div style={{ marginBottom: 16 }}>
          <Space align="center">
            <Text strong>拦截模式：</Text>
            <ToggleSwitch value={mode} onChange={handleModeChange} disabled={readonly} />
          </Space>
        </div>

        <div>
          <Space align="center" style={{ marginBottom: 8 }}>
            <Text strong>{mode === 'allow' ? `允许的${label}：` : `排除的${label}：`}</Text>
          </Space>

          <Card size="small">
            {/* 横向网格布局 */}
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(3, 1fr)', 
              gap: '12px',
              marginBottom: 12
            }}>
              {brandGroups.map((brandName) => {
                const models = deviceModelsByBrand[brandName];
                const modelOptions = Object.entries(models).map(([value, label]) => ({
                  value,
                  label: String(label)
                }));
                const brandSelectedItems = selectedItems.filter((item) =>
                  Object.keys(models).includes(item)
                );

                return (
                  <div key={brandName}>
                    <Text strong style={{ display: 'block', marginBottom: 4, fontSize: 12 }}>{brandName}</Text>
                    <Select
                      mode="multiple"
                      placeholder="选择型号"
                      style={{ width: '100%' }}
                      size="small"
                      value={brandSelectedItems}
                      onChange={(newBrandItems) => {
                        const otherItems = selectedItems.filter(
                          (item) => !Object.keys(models).includes(item)
                        );
                        const newItems = [...otherItems, ...newBrandItems];
                        handleItemChange(newItems);
                      }}
                      allowClear
                      disabled={readonly}
                      showSearch
                      optionFilterProp="children"
                      maxTagCount={1}
                    >
                      {modelOptions.map((option) => (
                        <Option key={option.value} value={option.value}>
                          {option.label}
                        </Option>
                      ))}
                    </Select>
                  </div>
                );
              })}
            </div>

            {/* 已选择的型号标签 */}
            {selectedItems.length > 0 && (
              <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 8 }}>
                <Text type="secondary" style={{ marginRight: 8 }}>已选择:</Text>
                {selectedItems.map((item) => (
                  <Tag
                    key={item}
                    closable={!readonly}
                    onClose={() => handleRemoveItem(item)}
                    color={mode === 'allow' ? 'green' : 'red'}
                    style={{ marginBottom: 4 }}
                  >
                    {item}
                  </Tag>
                ))}
              </div>
            )}
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Title level={5}>{label}</Title>

      <div style={{ marginBottom: 16 }}>
        <Space align="center">
          <Text strong>拦截模式：</Text>
          <ToggleSwitch value={mode} onChange={handleModeChange} disabled={readonly} />
        </Space>
      </div>

      <div>
        <Space align="center" style={{ marginBottom: 8 }}>
          <Text strong>{mode === 'allow' ? `允许的${label}：` : `排除的${label}：`}</Text>
        </Space>

        <Card size="small">
          {isTagInput ? (
            <Select
              mode="tags"
              placeholder={placeholder}
              style={{ width: '100%' }}
              value={selectedItems}
              onChange={handleItemChange}
              allowClear
              disabled={readonly}
              tokenSeparators={[',']}
            />
          ) : (
            <Select
              mode="multiple"
              placeholder={placeholder}
              style={{ width: '100%' }}
              value={selectedItems}
              onChange={handleItemChange}
              showSearch={showSearch}
              optionFilterProp="children"
              allowClear
              disabled={readonly}
            >
              {options.map((option) => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
          )}

          <div style={{ marginTop: 8 }}>
            {selectedItems.map((item) => (
              <Tag
                key={item}
                closable={!readonly}
                onClose={() => handleRemoveItem(item)}
                color={mode === 'allow' ? 'green' : 'red'}
                style={{ marginBottom: 4 }}
              >
                {renderTagText(item)}
              </Tag>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
});

// 统一的互斥配置组件
interface MutexConfigProps {
  mode?: 'allow' | 'block';
  onModeChange?: (mode: 'allow' | 'block') => void;
  allowList: string[];
  blockList: string[];
  onAllowListChange: (list: string[]) => void;
  onBlockListChange: (list: string[]) => void;
  options: Array<{value: string; label: string}>;
  placeholder: string;
  label: string;
  readonly?: boolean;
  showSearch?: boolean;
  treeData?: any[];
  getDisplayName?: (value: string) => string;
}

const MutexConfig: React.FC<MutexConfigProps> = ({
  mode,
  onModeChange,
  allowList,
  blockList,
  onAllowListChange,
  onBlockListChange,
  options,
  placeholder,
  label,
  readonly = false,
  showSearch = false,
  treeData,
  getDisplayName
}) => {
  // 如果传入了mode和onModeChange，使用受控模式
  // 否则使用非受控模式，自己管理状态
  const isControlled = mode !== undefined && onModeChange !== undefined;
  
  // 非受控模式：自己管理mode状态
  const [internalMode, setInternalMode] = useState<'allow' | 'block'>(() => {
    if (isControlled) return mode!;
    
    // 根据数据确定初始模式
    const hasBlocked = blockList && blockList.length > 0;
    const hasAllowed = allowList && allowList.length > 0;
    
    if (hasBlocked) {
      return 'block';
    } else if (hasAllowed) {
      return 'allow';
    } else {
      return 'allow'; // 默认允许模式
    }
  });
  
  const currentMode = isControlled ? mode! : internalMode;
  const currentList = currentMode === 'allow' ? allowList : blockList;
  const setCurrentList = currentMode === 'allow' ? onAllowListChange : onBlockListChange;
  
  // 添加调试日志
  console.log('🔍 MutexConfig render - isControlled:', isControlled);
  console.log('🔍 MutexConfig render - mode:', mode);
  console.log('🔍 MutexConfig render - internalMode:', internalMode);
  console.log('🔍 MutexConfig render - currentMode:', currentMode);
  console.log('🔍 MutexConfig render - allowList:', allowList);
  console.log('🔍 MutexConfig render - blockList:', blockList);
  console.log('🔍 MutexConfig render - currentList:', currentList);
  
  const handleModeChange = (newMode: 'allow' | 'block') => {
    console.log('🔍 MutexConfig handleModeChange - newMode:', newMode);
    console.log('🔍 MutexConfig handleModeChange - current mode:', currentMode);
    console.log('🔍 MutexConfig handleModeChange - isControlled:', isControlled);
    
    if (isControlled) {
      // 受控模式：调用外部回调
      onModeChange!(newMode);
    } else {
      // 非受控模式：更新内部状态
      setInternalMode(newMode);
    }
    
    // 切换模式时清空另一个列表
    if (newMode === 'allow') {
      console.log('🔍 MutexConfig handleModeChange - clearing blockList');
      onBlockListChange([]);
    } else {
      console.log('🔍 MutexConfig handleModeChange - clearing allowList');
      onAllowListChange([]);
    }
  };

  const addItem = (value: string) => {
    if (!value || value.trim() === '') return;
    const trimmedValue = value.trim();
    console.log('🔍 MutexConfig addItem - value:', trimmedValue, 'currentList:', currentList);
    if (!currentList.includes(trimmedValue)) {
      const newList = [...currentList, trimmedValue];
      console.log('🔍 MutexConfig addItem - newList:', newList);
      setCurrentList(newList);
    }
  };

  const removeItem = (index: number) => {
    const newList = currentList.filter((_, i) => i !== index);
    console.log('🔍 MutexConfig removeItem - index:', index, 'newList:', newList);
    setCurrentList(newList);
  };

  const getDisplayText = (value: string) => {
    if (getDisplayName) {
      return getDisplayName(value);
    }
    const option = options?.find(opt => opt.value === value);
    return option ? option.label : value;
  };

  return (
    <div>
      <Space align="center" style={{ marginBottom: 8 }}>
        <Text strong>{label}</Text>
        <Radio.Group 
          value={mode} 
          onChange={(e) => handleModeChange(e.target.value)}
          disabled={readonly}
        >
          <Radio.Button value="allow">允许</Radio.Button>
          <Radio.Button value="block">排除</Radio.Button>
        </Radio.Group>
      </Space>
      
      <Card size="small" style={{ marginBottom: 8 }}>
        <Space style={{ marginBottom: 8 }}>
          {treeData ? (
            <TreeSelect
              placeholder={placeholder}
              style={{ width: 360 }}
              treeData={treeData}
              onChange={(value) => addItem(String(value))}
              showSearch={showSearch}
              treeNodeFilterProp="title"
              allowClear
              treeDefaultExpandAll
              disabled={readonly}
            />
          ) : (
            <Select
              placeholder={placeholder}
              style={{ width: 300 }}
              onChange={(value) => addItem(String(value))}
              showSearch={showSearch}
              optionFilterProp="children"
              allowClear
              disabled={readonly}
            >
              {options?.map(option => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
          )}
        </Space>
        
        <div>
          {currentList.map((item, index) => (
            <Tag
              key={index}
              closable={!readonly}
              onClose={() => removeItem(index)}
              color={mode === 'allow' ? 'green' : 'red'}
              style={{ marginBottom: 4 }}
            >
              {getDisplayText(item)}
            </Tag>
          ))}
        </div>
      </Card>
    </div>
  );
};

interface RuleConfig {
  type: string;
  name: string;
  config?: any;
}

interface RuleEditModalProps {
  visible: boolean;
  rule: RuleConfig | null;
  allRules?: any[];
  linkId?: number;
  readonly?: boolean;
  onOk: (rule: RuleConfig) => void;
  onCancel: () => void;
  onSaveAll?: (allRules: any[]) => Promise<void> | void;
}

const RuleEditModal: React.FC<RuleEditModalProps> = ({
  visible,
  rule,
  allRules,
  linkId,
  readonly = false,
  onOk,
  onCancel,
  onSaveAll,
}) => {
  const [form] = Form.useForm();
  const [config, setConfig] = useState<any>({});
  const [saving, setSaving] = useState<boolean>(false);
  const { user } = useAuthStore();
  const isSystemAdmin = user?.user_type === 'admin';

  useEffect(() => {
    if (rule && visible) {
      console.log('🔍 RuleEditModal useEffect - rule:', rule);
      form.setFieldsValue({
        name: rule.name,
      });
      setConfig(rule.config || {});
      
      console.log('RuleEditModal useEffect - rule:', rule);
    }
  }, [rule, visible]);

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      // 互斥校验（设备规则）：同一维度 allow 与 block 不能同时配置
      if (rule?.type === 'device' || rule?.name === '设备检测' || (config.deviceTypeAllow || config.deviceTypeBlock || config.browserAllow || config.browserBlock)) {
        const conflicts: string[] = [];
        const checkPair = (allowKey: string, blockKey: string, label: string) => {
          const allowList = (config as any)[allowKey] || [];
          const blockList = (config as any)[blockKey] || [];
          if (allowList.length > 0 && blockList.length > 0) {
            conflicts.push(`${label}：已选择允许与排除，请仅保留其一`);
          }
        };
        checkPair('deviceTypeAllow', 'deviceTypeBlock', '设备类型');
        checkPair('browserAllow', 'browserBlock', '浏览器');
        checkPair('systemAllow', 'systemBlock', '系统类型');
        checkPair('clientTypeAllow', 'clientTypeBlock', '客户端类型');
        checkPair('mobileAppAllow', 'mobileAppBlock', '移动应用');
        if (conflicts.length > 0) {
          message.error(conflicts.join('；'));
          return;
        }
      }

      // 清理 config 中的无用字段（根据规则类型）
      let cleanedConfig = { ...config };
      if (rule?.type === 'url_referer') {
        // 只保留 refererList，删除其他无用字段
        const { refererList, ...rest } = cleanedConfig;
        cleanedConfig = { refererList };
      }

      // 移除前端临时使用的模式标记字段
      Object.keys(cleanedConfig).forEach((key) => {
        if (key.startsWith('__mode_')) {
          delete cleanedConfig[key];
        }
      });

      const updatedRule = {
        ...rule,
        ...values,
        config: cleanedConfig,
      };
      
      // 添加调试信息
      console.log('RuleEditModal handleOk - form values:', values);
      console.log('RuleEditModal handleOk - config:', config);
      console.log('RuleEditModal handleOk - cleanedConfig:', cleanedConfig);
      console.log('RuleEditModal handleOk - updatedRule:', updatedRule);
      
      // 如果有保存全部规则的回调，则保存全部规则
      if (onSaveAll && allRules) {
        setSaving(true);
        try {
          // 更新当前规则在全部规则中的配置
          const updatedAllRules = allRules.map(r => 
            r.type === updatedRule.type ? {
              ...r,
              is_active: updatedRule.isActive,
              isActive: updatedRule.isActive, // 确保两种字段名都同步
              config: updatedRule.config
            } : r
          );
          console.log('RuleEditModal handleOk - updatedAllRules:', updatedAllRules);
          const maybePromise = onSaveAll(updatedAllRules);
          if (maybePromise && typeof (maybePromise as Promise<void>).then === 'function') {
            await (maybePromise as Promise<void>);
          }
          // 保存成功，关闭弹窗
          onCancel();
        } finally {
          setSaving(false);
        }
      } else {
        // 否则只调用单个规则的保存
        onOk(updatedRule);
      }
    } catch (error) {
      console.error('RuleEditModal handleOk error:', error);
      message.error('请检查表单填写');
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setConfig({});
    onCancel();
  };

  // IP黑白名单配置组件
  const IPBlacklistConfig = ({ config, setConfig, readonly = false }: { config: any; setConfig: (config: any) => void; readonly?: boolean }) => {
    const [blacklist, setBlacklist] = useState<string[]>(config.blacklist || []);
    const [whitelist, setWhitelist] = useState<string[]>(config.whitelist || []);

    const addIP = (listType: 'blacklist' | 'whitelist', ip: string) => {
      if (readonly || !ip.trim()) return;
      
      const newIP = ip.trim();
      if (listType === 'blacklist') {
        if (!blacklist.includes(newIP)) {
          const newList = [...blacklist, newIP];
          setBlacklist(newList);
          setConfig({ ...config, blacklist: newList });
        }
      } else {
        if (!whitelist.includes(newIP)) {
          const newList = [...whitelist, newIP];
          setWhitelist(newList);
          setConfig({ ...config, whitelist: newList });
        }
      }
    };

    const removeIP = (listType: 'blacklist' | 'whitelist', index: number) => {
      if (readonly) return;
      
      if (listType === 'blacklist') {
        const newList = blacklist.filter((_, i) => i !== index);
        setBlacklist(newList);
        setConfig({ ...config, blacklist: newList });
      } else {
        const newList = whitelist.filter((_, i) => i !== index);
        setWhitelist(newList);
        setConfig({ ...config, whitelist: newList });
      }
    };

    return (
      <div>
        <Title level={5}>IP黑白名单配置</Title>
        
        {/* IP黑名单配置 */}
        <Card size="small" style={{ marginBottom: 16 }}>
          <div style={{ marginBottom: 8 }}>
            <Space>
              <Text strong>IP黑名单</Text>
            </Space>
          </div>
          
          <div>
            {!readonly && (
              <Space style={{ marginBottom: 8 }}>
                <Input
                  placeholder="输入IP地址或IP段 (如: 192.168.1.1 或 192.168.1.0/24)"
                  style={{ width: 300 }}
                  onPressEnter={(e) => addIP('blacklist', e.currentTarget.value)}
                />
                <Button 
                  type="primary" 
                  icon={<PlusOutlined />}
                  onClick={() => {
                    const input = document.querySelector('input[placeholder*="IP地址"]') as HTMLInputElement;
                    if (input) addIP('blacklist', input.value);
                  }}
                >
                  添加
                </Button>
              </Space>
            )}
            
            <div>
              {blacklist.map((ip, index) => (
                <Tag
                  key={index}
                  closable={!readonly}
                  onClose={readonly ? undefined : () => removeIP('blacklist', index)}
                  color="red"
                  style={{ marginBottom: 4 }}
                >
                  {ip}
                </Tag>
              ))}
            </div>
          </div>
        </Card>

        {/* IP白名单配置 */}
        <Card size="small">
          <div style={{ marginBottom: 8 }}>
            <Space>
              <Text strong>IP白名单</Text>
            </Space>
          </div>
          
          <div>
            <Space style={{ marginBottom: 8 }}>
              <Input
                placeholder="输入IP地址或IP段 (如: 192.168.1.1 或 192.168.1.0/24)"
                style={{ width: 300 }}
                onPressEnter={(e) => addIP('whitelist', e.currentTarget.value)}
              />
              <Button 
                type="primary" 
                icon={<PlusOutlined />}
                onClick={() => {
                  const input = document.querySelectorAll('input[placeholder*="IP地址"]')[1] as HTMLInputElement;
                  if (input) addIP('whitelist', input.value);
                }}
              >
                添加
              </Button>
            </Space>
            
            <div>
              {whitelist.map((ip, index) => (
                <Tag
                  key={index}
                  closable
                  onClose={() => removeIP('whitelist', index)}
                  color="green"
                  style={{ marginBottom: 4 }}
                >
                  {ip}
                </Tag>
              ))}
            </div>
          </div>
        </Card>
      </div>
    );
  };

  // 访客来源配置组件
  const VisitorSourceConfig = ({ config, setConfig, readonly = false }: { config: any; setConfig: (config: any) => void; readonly?: boolean }) => {
    const [refererList, setRefererList] = useState<string[]>(config.refererList || []);

    // 当config更新时，同步本地状态
    useEffect(() => {
      setRefererList(config.refererList || []);
    }, [config.refererList]);

    const addReferer = (referer: string) => {
      if (!referer.trim()) return;
      const newReferer = referer.trim();
      if (!refererList.includes(newReferer)) {
        const newList = [...refererList, newReferer];
        setRefererList(newList);
        setConfig({ ...config, refererList: newList });
      }
    };

    const removeReferer = (index: number) => {
      const newList = refererList.filter((_, i) => i !== index);
      setRefererList(newList);
      setConfig({ ...config, refererList: newList });
    };

    return (
      <div>
        <Title level={5}>访客来源配置</Title>
        
        <Card size="small">
          <div style={{ marginBottom: 8 }}>
            <Space>
              <Text strong>Referer来源控制</Text>
            </Space>
          </div>
          
          <div>
            <Space style={{ marginBottom: 8 }}>
              <Input
                placeholder="输入允许的Referer域名 (如: example.com)"
                style={{ width: 300 }}
                onPressEnter={(e) => addReferer(e.currentTarget.value)}
              />
              <Button 
                type="primary" 
                icon={<PlusOutlined />}
                onClick={() => {
                  const input = document.querySelector('input[placeholder*="Referer域名"]') as HTMLInputElement;
                  if (input) addReferer(input.value);
                }}
              >
                添加
              </Button>
            </Space>
            
            <div>
              {refererList.map((referer, index) => (
                <Tag
                  key={index}
                  closable
                  onClose={() => removeReferer(index)}
                  color="blue"
                  style={{ marginBottom: 4 }}
                >
                  {referer}
                </Tag>
              ))}
            </div>
          </div>
        </Card>
      </div>
    );
  };

  // 国家地区拦截配置组件（简化版：先选模式，再选选项）
  const GeographyConfig = ({ config, setConfig, readonly = false }: { config: any; setConfig: (config: any) => void; readonly?: boolean }) => {
    const { constants, loading, error } = useDeviceConstants();

    const modeKey = '__mode_geography';
    const storedMode = config?.[modeKey];
    const hasBlocked = Array.isArray(config.blockedCountries) && config.blockedCountries.length > 0;
    const hasAllowed = Array.isArray(config.allowedCountries) && config.allowedCountries.length > 0;
    const defaultMode: 'allow' | 'block' = hasAllowed ? 'allow' : (hasBlocked ? 'block' : 'block');
    const mode: 'allow' | 'block' = storedMode === 'allow' || storedMode === 'block' ? storedMode : defaultMode;

    const selectedCountriesRaw = useMemo<string[]>(() => {
      if (mode === 'allow') {
        return Array.isArray(config.allowedCountries) ? config.allowedCountries : [];
      }
      return Array.isArray(config.blockedCountries) ? config.blockedCountries : [];
    }, [mode, config.allowedCountries, config.blockedCountries]);

    const getCountryName = (countryCode: string) => {
      // 从后端数据中获取国家名称
      if (constants?.country_codes) {
        return constants.country_codes[countryCode] || countryCode;
      }
      return countryCode;
    };

    const regionCountryMap = useMemo<Record<string, string[]>>(() => ({
        '亚洲': ['CN', 'JP', 'KR', 'IN', 'TH', 'VN', 'SG', 'MY', 'ID', 'PH', 'TW', 'HK', 'MO', 'MN', 'KP', 'MM', 'KH', 'LA', 'BD', 'LK', 'PK', 'AF', 'IR', 'IQ', 'IL', 'SA', 'AE', 'TR', 'KZ', 'UZ', 'KG', 'TJ', 'TM'],
        '欧洲': ['RU', 'DE', 'FR', 'GB', 'IT', 'ES', 'NL', 'BE', 'CH', 'AT', 'SE', 'NO', 'DK', 'FI', 'PL', 'CZ', 'HU', 'RO', 'BG', 'GR', 'PT', 'IE', 'UA', 'BY', 'LT', 'LV', 'EE'],
        '北美洲': ['US', 'CA', 'MX'],
        '南美洲': ['BR', 'AR', 'CL', 'CO', 'PE', 'VE', 'EC', 'UY', 'PY', 'BO'],
        '非洲': ['ZA', 'EG', 'NG', 'KE', 'MA', 'DZ', 'TN', 'ET', 'GH', 'TZ'],
        '大洋洲': ['AU', 'NZ', 'FJ', 'PG']
    }), []);

    // 构建国家树形数据
    const buildCountryTreeData = () => {
      if (!constants?.country_codes) {
        return [];
      }

      return Object.entries(regionCountryMap).map(([regionName, countryCodes]) => ({
        title: regionName,
        value: regionName,
        key: regionName,
        children: countryCodes
          .filter(code => constants.country_codes[code]) // 只包含后端支持的国家
          .map(code => ({
            title: `${constants.country_codes[code]} (${code})`,
            value: code,
            key: code,
          }))
      }));
    };

    const normalizeCountrySelection = useCallback((values: string[]): string[] => {
      if (!Array.isArray(values) || values.length === 0) {
        return [];
      }

      const availableCountries = constants?.country_codes ?? {};
      const normalized: string[] = [];
      const seen = new Set<string>();

      values.forEach((value) => {
        const trimmedValue = (value || '').trim();
        if (!trimmedValue) {
          return;
        }

        const regionCountries = regionCountryMap[trimmedValue];
        if (Array.isArray(regionCountries)) {
          regionCountries.forEach((code) => {
            if (!availableCountries[code] || seen.has(code)) {
              return;
            }
            seen.add(code);
            normalized.push(code);
          });
          return;
        }

        const upperCode = trimmedValue.toUpperCase();
        if (availableCountries[upperCode] && !seen.has(upperCode)) {
          seen.add(upperCode);
          normalized.push(upperCode);
        }
      });

      return normalized;
    }, [constants?.country_codes, regionCountryMap]);

    const selectedCountries = useMemo<string[]>(
      () => normalizeCountrySelection(selectedCountriesRaw),
      [normalizeCountrySelection, selectedCountriesRaw],
    );

    useEffect(() => {
      if (!constants?.country_codes || selectedCountriesRaw.length === 0) {
        return;
      }

      const hasRegionValue = selectedCountriesRaw.some((value) => {
        const trimmedValue = (value || '').trim();
        return !!regionCountryMap[trimmedValue];
      });

      const hasUnknownValue = selectedCountriesRaw.some((value) => {
        const trimmedValue = (value || '').trim();
        if (!trimmedValue || regionCountryMap[trimmedValue]) {
          return false;
        }
        const upperCode = trimmedValue.toUpperCase();
        return !constants.country_codes?.[upperCode];
      });

      if (hasRegionValue || hasUnknownValue) {
        setConfig((prev: any) => ({
          ...prev,
          [modeKey]: mode,
          allowedCountries: mode === 'allow' ? selectedCountries : [],
          blockedCountries: mode === 'block' ? selectedCountries : [],
        }));
      }
    }, [constants?.country_codes, mode, modeKey, regionCountryMap, selectedCountries, selectedCountriesRaw, setConfig]);

    const handleModeChange = (newMode: 'allow' | 'block') => {
      setConfig((prev: any) => ({
        ...prev,
        [modeKey]: newMode,
        allowedCountries: newMode === 'allow' ? selectedCountries : [],
        blockedCountries: newMode === 'block' ? selectedCountries : [],
      }));
    };

    const handleCountryChange = (countries: string[]) => {
      const normalizedCountries = normalizeCountrySelection(countries);
      setConfig((prev: any) => ({
        ...prev,
        [modeKey]: mode,
        allowedCountries: mode === 'allow' ? normalizedCountries : [],
        blockedCountries: mode === 'block' ? normalizedCountries : [],
      }));
    };

    // 如果正在加载或出错，显示相应状态
    if (loading) {
      return (
        <div>
          <Title level={5}>国家地区拦截配置</Title>
          <Spin size="small" />
          <Text type="secondary" style={{ marginLeft: 8 }}>加载国家数据中...</Text>
        </div>
      );
    }

    if (error) {
      return (
        <div>
          <Title level={5}>国家地区拦截配置</Title>
          <Alert
            message="加载国家数据失败"
            description={error}
            type="error"
            showIcon
          />
        </div>
      );
    }

    if (!constants?.country_codes) {
      return (
        <div>
          <Title level={5}>国家地区拦截配置</Title>
          <Alert
            message="国家数据未初始化"
            description="请确保已登录并加载设备常量"
            type="warning"
            showIcon
          />
        </div>
      );
    }

    return (
      <div>
        <Title level={5}>国家地区拦截配置</Title>
        
        {/* 拦截模式选择 */}
        <div style={{ marginBottom: 16 }}>
          <Space align="center">
            <Text strong>拦截模式：</Text>
            <ToggleSwitch value={mode} onChange={handleModeChange} disabled={readonly} />
          </Space>
        </div>
        
        {/* 国家地区选择 */}
        <div>
          <Space align="center" style={{ marginBottom: 8 }}>
            <Text strong>{mode === 'allow' ? '允许的国家/地区：' : '排除的国家/地区：'}</Text>
          </Space>
          
          <Card size="small">
            <TreeSelect
              multiple
              placeholder="选择国家/地区"
              style={{ width: '100%' }}
              value={selectedCountries}
              onChange={handleCountryChange}
              treeData={buildCountryTreeData()}
              showSearch={true}
              treeNodeFilterProp="title"
              allowClear
              treeDefaultExpandAll
              disabled={readonly}
            />
            
            {/* 显示已选择的国家/地区 */}
            <div style={{ marginTop: 8 }}>
              {selectedCountries.map((country) => (
                <Tag
                  key={country}
                  closable={!readonly}
                  onClose={() => {
                    const newCountries = selectedCountries.filter(c => c !== country);
                    handleCountryChange(newCountries);
                  }}
                  color={mode === 'allow' ? 'green' : 'red'}
                  style={{ marginBottom: 4 }}
                >
                  {getCountryName(country)} ({country})
                </Tag>
              ))}
            </div>
          </Card>
        </div>
      </div>
    );
  };

  // 网络安全检测配置组件
  const NetworkConfig = ({ config, setConfig, readonly = false }: { config: any; setConfig: (config: any) => void; readonly?: boolean }) => {
    const [proxyDetection, setProxyDetection] = useState(config.proxyDetection !== false);
    const [datacenterDetection, setDatacenterDetection] = useState(config.datacenterDetection !== false);
    const [vpnDetection, setVpnDetection] = useState(config.vpnDetection !== false);
    const [satelliteDetection, setSatelliteDetection] = useState(config.satelliteDetection !== false);
    const [mobileNetworkDetection, setMobileNetworkDetection] = useState(config.mobileNetworkDetection !== false);
    const [icloudProxyDetection, setIcloudProxyDetection] = useState(config.icloudProxyDetection !== false);

    const handleProxyDetectionToggle = (checked: boolean) => {
      setProxyDetection(checked);
      setConfig({ ...config, proxyDetection: checked });
    };

    const handleDatacenterDetectionToggle = (checked: boolean) => {
      setDatacenterDetection(checked);
      setConfig({ ...config, datacenterDetection: checked });
    };

    const handleVpnDetectionToggle = (checked: boolean) => {
      setVpnDetection(checked);
      setConfig({ ...config, vpnDetection: checked });
    };

    const handleSatelliteDetectionToggle = (checked: boolean) => {
      setSatelliteDetection(checked);
      setConfig({ ...config, satelliteDetection: checked });
    };

    const handleMobileNetworkDetectionToggle = (checked: boolean) => {
      setMobileNetworkDetection(checked);
      setConfig({ ...config, mobileNetworkDetection: checked });
    };

    const handleIcloudProxyDetectionToggle = (checked: boolean) => {
      setIcloudProxyDetection(checked);
      setConfig({ ...config, icloudProxyDetection: checked });
    };

    return (
      <div>
        <Card title="网络安全检测配置" size="small">
          <Space direction="vertical" style={{ width: '100%' }} size="large">
            
            {/* 代理检测 */}
            <div>
              <Space align="center" style={{ marginBottom: 8 }}>
                <Switch
                  checked={proxyDetection}
                  onChange={handleProxyDetectionToggle}
                />
                <Text strong>代理服务器检测</Text>
                <Text type="secondary">检测并拦截代理服务器访问</Text>
              </Space>
              {proxyDetection && (
                <div style={{ marginLeft: 24 }}>
                  <Text type="secondary">启用后将检测并拦截来自代理服务器的访问请求</Text>
                </div>
              )}
            </div>

            {/* 数据中心检测 */}
            <div>
              <Space align="center" style={{ marginBottom: 8 }}>
                <Switch
                  checked={datacenterDetection}
                  onChange={handleDatacenterDetectionToggle}
                />
                <Text strong>数据中心检测</Text>
                <Text type="secondary">检测并拦截数据中心IP访问</Text>
              </Space>
              {datacenterDetection && (
                <div style={{ marginLeft: 24 }}>
                  <Text type="secondary">启用后将检测并拦截来自数据中心、机房、云服务商的访问</Text>
                </div>
              )}
            </div>

            {/* VPN检测 */}
            <div>
              <Space align="center" style={{ marginBottom: 8 }}>
                <Switch
                  checked={vpnDetection}
                  onChange={handleVpnDetectionToggle}
                />
                <Text strong>VPN检测</Text>
                <Text type="secondary">检测并拦截VPN连接访问</Text>
              </Space>
              {vpnDetection && (
                <div style={{ marginLeft: 24 }}>
                  <Text type="secondary">启用后将检测并拦截来自VPN服务的访问请求</Text>
                </div>
              )}
            </div>

            {/* 卫星网络检测 */}
            <div>
              <Space align="center" style={{ marginBottom: 8 }}>
                <Switch
                  checked={satelliteDetection}
                  onChange={handleSatelliteDetectionToggle}
                />
                <Text strong>卫星网络检测</Text>
                <Text type="secondary">检测并拦截卫星网络访问</Text>
              </Space>
              {satelliteDetection && (
                <div style={{ marginLeft: 24 }}>
                  <Text type="secondary">启用后将检测并拦截来自卫星网络的访问请求</Text>
                </div>
              )}
            </div>

            {/* 移动网络检测 */}
            {/* <div>
              <Space align="center" style={{ marginBottom: 8 }}>
                <Switch
                  checked={mobileNetworkDetection}
                  onChange={handleMobileNetworkDetectionToggle}
                />
                <Text strong>移动网络检测</Text>
                <Text type="secondary">检测并拦截移动网络访问</Text>
              </Space>
              {mobileNetworkDetection && (
                <div style={{ marginLeft: 24 }}>
                  <Text type="secondary">启用后将检测并拦截来自移动网络运营商的访问</Text>
                </div>
              )}
            </div> */}

            {/* iCloud专用代理检测 */}
            <div>
              <Space align="center" style={{ marginBottom: 8 }}>
                <Switch
                  checked={icloudProxyDetection}
                  onChange={handleIcloudProxyDetectionToggle}
                />
                <Text strong>iCloud专用代理检测</Text>
                <Text type="secondary">检测并拦截iCloud专用代理访问</Text>
              </Space>
              {icloudProxyDetection && (
                <div style={{ marginLeft: 24 }}>
                  <Text type="secondary">启用后将检测并拦截来自iCloud专用代理的访问请求</Text>
                </div>
              )}
            </div>

          </Space>
        </Card>
      </div>
    );
  };

  // 设备检测配置组件（简化版：先选模式，再选选项）
  const DeviceConfig = ({ config, setConfig, readonly = false }: { config: any; setConfig: (config: any) => void; readonly?: boolean }) => {
    const { constants, loading, error } = useDeviceConstants();

    // 如果数据未加载或出错，显示加载状态或错误信息
    if (loading) {
      return (
        <Card title="设备检测配置" size="small">
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <Spin size="large" />
            <div style={{ marginTop: '10px' }}>正在加载设备常量...</div>
          </div>
        </Card>
      );
    }

    if (error) {
      return (
        <Card title="设备检测配置" size="small">
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <Alert
              message="加载设备常量失败"
              description={error}
              type="error"
              showIcon
            />
          </div>
        </Card>
      );
    }

    if (!constants) {
      return (
        <Card title="设备检测配置" size="small">
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <Alert
              message="设备常量未初始化"
              description="请先登录以加载设备常量"
              type="warning"
              showIcon
            />
          </div>
        </Card>
      );
    }

    // 从后端获取设备类型选项
    const deviceTypeOptions = Object.entries(constants.device_types).map(([value, label]) => ({
      value,
      label: String(label)
    }));

    // 从后端获取系统类型选项
    const systemOptions = Object.entries(constants.os_types).map(([value, label]) => ({
      value,
      label: String(label)
    }));

    // 从后端获取设备品牌选项
    const brandOptions = Object.entries(constants.device_brands).map(([value, label]) => ({
      value,
      label: String(label)
    }));

    // 从后端获取客户端类型选项
    const clientTypeOptions = Object.entries(constants.client_types).map(([value, label]) => ({
      value,
      label: String(label)
    }));

    // 从后端获取浏览器类型选项
    const browserOptions = Object.entries(constants.browser_types).map(([value, label]) => ({
      value,
      label: String(label)
    }));

    // 从后端获取移动应用选项（使用专用的 mobile_apps 常量）
    const mobileAppOptions = Object.entries(constants.mobile_apps).map(([value, label]) => ({
      value,
      label: String(label)
    }));

    // 从后端获取 iOS 版本选项
    const iosVersionOptions = Object.entries(constants.ios_versions || {}).map(([value, label]) => ({
      value,
      label: String(label)
    }));

    // 从后端获取 Android 版本选项
    const androidVersionOptions = Object.entries(constants.android_versions || {}).map(([value, label]) => ({
      value,
      label: String(label)
    }));

    // 从后端获取设备型号选项
    const deviceModelOptions = Object.entries(constants.device_models || {}).map(([value, label]) => ({
      value,
      label: String(label)
    }));

    // 设备配置项
    const deviceConfigs = [
      {
        key: 'system',
        label: '操作系统',
        options: systemOptions,
        allowKey: 'systemAllow',
        blockKey: 'systemBlock',
        placeholder: '选择操作系统',
        isGroupedOS: true,
      },
      {
        key: 'osVersion',
        label: '系统版本',
        options: [],
        allowKey: 'osVersionAllow',
        blockKey: 'osVersionBlock',
        placeholder: '',
        allowInput: true,
        isTagInput: true,
        isGroupedOsVersion: true,
      },
      {
        key: 'deviceType',
        label: '设备类型',
        options: deviceTypeOptions,
        allowKey: 'deviceTypeAllow',
        blockKey: 'deviceTypeBlock',
        placeholder: '选择设备类型'
      },
      {
        key: 'deviceBrand',
        label: '设备品牌',
        options: brandOptions,
        allowKey: 'deviceBrandAllow',
        blockKey: 'deviceBrandBlock',
        placeholder: '选择设备品牌（如 Apple, Huawei, Xiaomi）',
        showSearch: true,
        allowInput: true,
      },
      {
        key: 'deviceModel',
        label: '设备型号',
        options: deviceModelOptions,
        allowKey: 'deviceModelAllow',
        blockKey: 'deviceModelBlock',
        placeholder: '选择设备型号',
        isGroupedDeviceModel: true,
      },
      {
        key: 'browser',
        label: '浏览器',
        options: browserOptions,
        allowKey: 'browserAllow',
        blockKey: 'browserBlock',
        placeholder: '选择浏览器'
      },
      {
        key: 'clientType',
        label: '客户端类型',
        options: clientTypeOptions,
        allowKey: 'clientTypeAllow',
        blockKey: 'clientTypeBlock',
        placeholder: '选择客户端类型'
      },
      {
        key: 'mobileApp',
        label: '移动应用',
        options: mobileAppOptions,
        allowKey: 'mobileAppAllow',
        blockKey: 'mobileAppBlock',
        placeholder: '选择移动应用'
      },
    ];

    // 单个设备配置项组件
    return (
      <div>
        <Card title="设备检测配置" size="small">
          <Space direction="vertical" style={{ width: '100%' }} size="large">
            {deviceConfigs.map(({ key, label, options, allowKey, blockKey, placeholder, showSearch, isTagInput, isGroupedOsVersion, isGroupedDeviceModel, isGroupedOS }) => {
              const modeKey = `__mode_${key}`;
              const storedMode = (config[modeKey] as DeviceMode | undefined);
              const currentMode = storedMode ?? resolveDeviceMode(config[allowKey], config[blockKey], 'block');
              return (
                <DeviceConfigItem
                  key={key}
                  label={label}
                  options={options}
                  allowList={config[allowKey] || []}
                  blockList={config[blockKey] || []}
                  placeholder={placeholder}
                  showSearch={showSearch}
                  readonly={readonly}
                  mode={currentMode}
                  isTagInput={isTagInput}
                  isGroupedOsVersion={isGroupedOsVersion}
                  isGroupedDeviceModel={isGroupedDeviceModel}
                  isGroupedOS={isGroupedOS}
                  iosVersionOptions={iosVersionOptions}
                  androidVersionOptions={androidVersionOptions}
                  deviceModelsByBrand={constants.device_models_by_brand || {}}
                  onModeChange={(nextMode, items) => {
                    setConfig((prevConfig: any) => ({
                      ...prevConfig,
                      [modeKey]: nextMode,
                      [allowKey]: nextMode === 'allow' ? items : [],
                      [blockKey]: nextMode === 'block' ? items : [],
                    }));
                  }}
                  onItemsChange={(modeForItems, items) => {
                    setConfig((prevConfig: any) => ({
                      ...prevConfig,
                      [modeKey]: modeForItems,
                      [allowKey]: modeForItems === 'allow' ? items : [],
                      [blockKey]: modeForItems === 'block' ? items : [],
                    }));
                  }}
                />
              );
            })}
          </Space>
        </Card>
      </div>
    );
  };

  // 语言拦截配置组件（简化版：先选模式，再选选项）
  const LanguageConfig = ({ config, setConfig, readonly = false }: { config: any; setConfig: (config: any) => void; readonly?: boolean }) => {
    const { constants, loading, error } = useDeviceConstants();

    // 如果数据未加载或出错，显示加载状态或错误信息
    if (loading) {
      return (
        <Card title="语言拦截配置" size="small">
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <Spin size="large" />
            <div style={{ marginTop: '10px' }}>正在加载语言常量...</div>
          </div>
        </Card>
      );
    }

    if (error) {
      return (
        <Card title="语言拦截配置" size="small">
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <Alert
              message="加载语言常量失败"
              description={error}
              type="error"
              showIcon
            />
          </div>
        </Card>
      );
    }

    if (!constants) {
      return (
        <Card title="语言拦截配置" size="small">
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <Alert
              message="语言常量未初始化"
              description="请先登录以加载语言常量"
              type="warning"
              showIcon
            />
          </div>
        </Card>
      );
    }
    const modeKey = '__mode_language';
    const storedMode = config?.[modeKey];
    const hasBlocked = Array.isArray(config.blockedLanguages) && config.blockedLanguages.length > 0;
    const hasAllowed = Array.isArray(config.allowedLanguages) && config.allowedLanguages.length > 0;
    const defaultMode: 'allow' | 'block' = hasAllowed ? 'allow' : (hasBlocked ? 'block' : 'allow');
    const mode: 'allow' | 'block' = storedMode === 'allow' || storedMode === 'block' ? storedMode : defaultMode;

    const selectedLanguages = useMemo<string[]>(() => {
      if (mode === 'allow') {
        return Array.isArray(config.allowedLanguages) ? config.allowedLanguages : [];
      }
      return Array.isArray(config.blockedLanguages) ? config.blockedLanguages : [];
    }, [mode, config.allowedLanguages, config.blockedLanguages]);

    const getLanguageName = (languageCode: string) => {
      // 从后端数据中获取语言名称
      return constants.language_codes[languageCode] || languageCode;
    };

    const handleModeChange = (newMode: 'allow' | 'block') => {
      setConfig((prev: any) => ({
        ...prev,
        [modeKey]: newMode,
        allowedLanguages: newMode === 'allow' ? selectedLanguages : [],
        blockedLanguages: newMode === 'block' ? selectedLanguages : [],
      }));
    };

    const handleLanguageChange = (languages: string[]) => {
      setConfig((prev: any) => ({
        ...prev,
        [modeKey]: mode,
        allowedLanguages: mode === 'allow' ? languages : [],
        blockedLanguages: mode === 'block' ? languages : [],
      }));
    };

    return (
      <div>
        <Title level={5}>语言拦截配置</Title>
        
        {/* 拦截模式选择 */}
        <div style={{ marginBottom: 16 }}>
          <Space align="center">
            <Text strong>拦截模式：</Text>
            <ToggleSwitch value={mode} onChange={handleModeChange} disabled={readonly} />
          </Space>
        </div>
        
        {/* 语言选择 */}
        <div>
          <Space align="center" style={{ marginBottom: 8 }}>
            <Text strong>{mode === 'allow' ? '允许的语言：' : '排除的语言：'}</Text>
          </Space>
          
          <Card size="small">
            <Select
              mode="multiple"
              placeholder="选择语言"
              style={{ width: '100%' }}
              value={selectedLanguages}
              onChange={handleLanguageChange}
              showSearch={true}
              optionFilterProp="children"
              allowClear
              disabled={readonly}
            >
              {Object.entries(constants.language_codes).map(([value, label]) => (
                <Option key={value} value={value}>
                  {String(label)}
                </Option>
              ))}
            </Select>
            
            {/* 显示已选择的语言 */}
            <div style={{ marginTop: 8 }}>
              {selectedLanguages.map((language) => (
                <Tag
                  key={language}
                  closable={!readonly}
                  onClose={() => {
                    const newLanguages = selectedLanguages.filter(l => l !== language);
                    handleLanguageChange(newLanguages);
                  }}
                  color={mode === 'allow' ? 'green' : 'red'}
                  style={{ marginBottom: 4 }}
                >
                  {getLanguageName(language)}
                </Tag>
              ))}
            </div>
          </Card>
        </div>
      </div>
    );
  };

  // UTM参数拦截配置组件（简化版：直接下拉选择）
  const UTMConfig = ({ config, setConfig, readonly = false }: { config: any; setConfig: (config: any) => void; readonly?: boolean }) => {
    const { constants, loading, error } = useDeviceConstants(); // ✅ 获取常量和加载状态
    
    const [utmSourceAllowed, setUtmSourceAllowed] = useState<string[]>(config.utmSourceAllowed || []);
    const [utmMediumAllowed, setUtmMediumAllowed] = useState<string[]>(config.utmMediumAllowed || []);
    const [utmCampaignAllowed, setUtmCampaignAllowed] = useState<string[]>(config.utmCampaignAllowed || []);
    const [utmTermAllowed, setUtmTermAllowed] = useState<string[]>(config.utmTermAllowed || []);
    const [utmContentAllowed, setUtmContentAllowed] = useState<string[]>(config.utmContentAllowed || []);

    const normalizeValues = (values: string[]) => {
      const seen = new Set<string>();
      const result: string[] = [];
      values.forEach((val) => {
        const trimmed = (val ?? '').trim();
        if (trimmed && !seen.has(trimmed)) {
          seen.add(trimmed);
          result.push(trimmed);
        }
      });
      return result;
    };

    const handleUtmChange = (type: string, values: string[]) => {
      const normalized = normalizeValues(values);
      switch (type) {
        case 'utmSource':
          setUtmSourceAllowed(normalized);
          setConfig({ ...config, utmSourceAllowed: normalized });
          break;
        case 'utmMedium':
          setUtmMediumAllowed(normalized);
          setConfig({ ...config, utmMediumAllowed: normalized });
          break;
        case 'utmCampaign':
          setUtmCampaignAllowed(normalized);
          setConfig({ ...config, utmCampaignAllowed: normalized });
          break;
        case 'utmTerm':
          setUtmTermAllowed(normalized);
          setConfig({ ...config, utmTermAllowed: normalized });
          break;
        case 'utmContent':
          setUtmContentAllowed(normalized);
          setConfig({ ...config, utmContentAllowed: normalized });
          break;
      }
    };

    // 如果正在加载或出错，显示相应状态
    if (loading) {
      return (
        <div>
          <Title level={5}>UTM参数拦截配置</Title>
          <Spin size="small" />
          <Text type="secondary" style={{ marginLeft: 8 }}>加载UTM数据中...</Text>
        </div>
      );
    }

    if (error) {
      return (
        <div>
          <Title level={5}>UTM参数拦截配置</Title>
          <Alert
            message="加载UTM数据失败"
            description={error}
            type="error"
            showIcon
          />
        </div>
      );
    }

    if (!constants?.utm_sources || !constants?.utm_mediums || !constants?.utm_campaigns || !constants?.utm_terms || !constants?.utm_contents) {
      return (
        <div>
          <Title level={5}>UTM参数拦截配置</Title>
          <Alert
            message="UTM数据未初始化"
            description="请确保已登录并加载设备常量"
            type="warning"
            showIcon
          />
        </div>
      );
    }

    const utmTypes = [
      { 
        key: 'utmSource', 
        label: 'UTM来源 (utm_source)', 
        list: utmSourceAllowed,
        options: Object.keys(constants.utm_sources),
        labels: constants.utm_sources
      },
      { 
        key: 'utmMedium', 
        label: 'UTM媒介 (utm_medium)', 
        list: utmMediumAllowed,
        options: Object.keys(constants.utm_mediums),
        labels: constants.utm_mediums
      },
      { 
        key: 'utmCampaign', 
        label: 'UTM活动 (utm_campaign)', 
        list: utmCampaignAllowed,
        options: Object.keys(constants.utm_campaigns),
        labels: constants.utm_campaigns
      },
      { 
        key: 'utmTerm', 
        label: 'UTM关键词 (utm_term)', 
        list: utmTermAllowed,
        options: Object.keys(constants.utm_terms),
        labels: constants.utm_terms
      },
      { 
        key: 'utmContent', 
        label: 'UTM内容 (utm_content)', 
        list: utmContentAllowed,
        options: Object.keys(constants.utm_contents),
        labels: constants.utm_contents
      },
    ];

    return (
      <Card title="UTM参数拦截配置" size="small">
        <Space direction="vertical" style={{ width: '100%' }}>
          <Alert
            message="UTM参数配置说明"
            description="只支持允许功能：只有匹配允许列表中的UTM参数的访客才能访问"
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />
          
          {utmTypes.map(({ key, label, list, options, labels }) => (
            <div key={key}>
              <Space align="center" style={{ marginBottom: 8 }}>
                <Text strong>{label}：</Text>
              </Space>
              
              <Card size="small">
                <Select
                  mode="tags"
                  placeholder="选择或输入UTM参数值"
                  style={{ width: '100%' }}
                  value={list}
                  onChange={(values) => handleUtmChange(key, values as string[])}
                  showSearch
                  optionFilterProp="children"
                  allowClear
                  disabled={readonly}
                  filterOption={(input, option) => {
                    if (!option) return false;
                    const childText = (option as any).children?.toString().toLowerCase() || '';
                    const valueText = (option as any).value?.toString().toLowerCase() || '';
                    const target = input.toLowerCase();
                    return childText.includes(target) || valueText.includes(target);
                  }}
                  tokenSeparators={[',', ' ', '\n']}
                >
                  {options.map((value: string) => (
                    <Option key={value} value={value}>
                      {labels[value] || value}
                    </Option>
                  ))}
                </Select>
              </Card>
            </div>
          ))}
        </Space>
      </Card>
    );
  };

  // 广告平台配置组件
  const AdvertisingConfig = ({ config, setConfig, readonly = false }: { config: any; setConfig: (config: any) => void; readonly?: boolean }) => {
    const { constants, loading, error } = useDeviceConstants(); // 获取广告平台常量
    const [requiredPlatforms, setRequiredPlatforms] = useState<string[]>(config.requiredPlatforms || []);

    // 将广告平台常量转换为选项数组
    const adPlatforms = constants?.ad_platforms
      ? Object.entries(constants.ad_platforms).map(([value, label]) => ({
          value,
          label,
        }))
      : [];

    const handlePlatformChange = (platforms: string[]) => {
      setRequiredPlatforms(platforms);
      setConfig({ ...config, requiredPlatforms: platforms });
    };

    // 如果正在加载或出错，显示相应状态
    if (loading) {
      return (
        <Card title="广告平台拦截配置" size="small">
          <Spin size="small" />
          <Text type="secondary" style={{ marginLeft: 8 }}>加载广告平台数据中...</Text>
        </Card>
      );
    }

    if (error) {
      return (
        <Card title="广告平台拦截配置" size="small">
          <Alert
            message="加载广告平台数据失败"
            description={error}
            type="error"
            showIcon
          />
        </Card>
      );
    }

    if (!adPlatforms || adPlatforms.length === 0) {
      return (
        <Card title="广告平台拦截配置" size="small">
          <Alert
            message="广告平台数据未初始化"
            description="请确保已登录并加载设备常量"
            type="warning"
            showIcon
          />
        </Card>
      );
    }

    return (
      <Card title="广告平台拦截配置" size="small">
        <Space direction="vertical" style={{ width: '100%' }}>
          {isSystemAdmin && (
            <Alert
              message="广告平台配置说明"
              description="访客必须携带选中平台的广告参数才能访问，未选择任何平台时规则不生效"
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}
          
          <div>
            <Text strong style={{ display: 'block', marginBottom: 8 }}>
              访客需要携带对应广告平台特征：
            </Text>
            <Select
              mode="multiple"
              placeholder="选择广告平台（可多选）"
              style={{ width: '100%' }}
              value={requiredPlatforms}
              onChange={handlePlatformChange}
              disabled={readonly}
              allowClear
            >
              {adPlatforms.map(platform => (
                <Select.Option key={platform.value} value={platform.value}>
                  {platform.label}
                </Select.Option>
              ))}
            </Select>
            {requiredPlatforms.length > 0 && (
              <div style={{ marginTop: 8 }}>
                <Text type="secondary">
                  已选择 {requiredPlatforms.length} 个平台：{requiredPlatforms.map(p => {
                    const platform = adPlatforms.find(ap => ap.value === p);
                    return platform ? platform.label : p;
                  }).join(', ')}
                </Text>
              </div>
            )}
          </div>

          {isSystemAdmin && (
            <div style={{ marginTop: 16 }}>
              <Alert
                type="info"
                message="提示"
                description="系统会自动识别常见广告平台的归因参数，无需在页面暴露具体字段。访客只要来自所选平台即可通过。"
                showIcon
              />
            </div>
          )}
        </Space>
      </Card>
    );
  };

  const ASNConfig = ({ config, setConfig, readonly = false }: { config: any; setConfig: (config: any) => void; readonly?: boolean }) => {
    const [allowedASNs, setAllowedASNs] = useState<string[]>(config.allowedASNs || []);
    const [blockedASNs, setBlockedASNs] = useState<string[]>(config.blockedASNs || []);

    const normalizeASNInput = (value: string) => {
      if (!value) return '';
      const trimmed = value.trim();
      if (!trimmed) return '';
      if (/^as\d+$/i.test(trimmed)) {
        return `AS${trimmed.replace(/^as/i, '')}`;
      }
      if (/^\d+$/.test(trimmed)) {
        return `AS${trimmed}`;
      }
      return trimmed.replace(/\s+/g, ' ').trim();
    };

    const dedupeList = (values: string[]) => {
      const result: string[] = [];
      const seen = new Set<string>();
      values.forEach((item) => {
        const normalized = normalizeASNInput(item);
        if (!normalized) return;
        const key = normalized.toLowerCase();
        if (!seen.has(key)) {
          seen.add(key);
          result.push(normalized);
        }
      });
      return result;
    };

    const handleAllowedChange = (values: string[]) => {
      const normalized = dedupeList(values);
      setAllowedASNs(normalized);
      setConfig({
        ...config,
        allowedASNs: normalized,
        blockedASNs,
      });
    };

    const handleBlockedChange = (values: string[]) => {
      const normalized = dedupeList(values);
      setBlockedASNs(normalized);
      setConfig({
        ...config,
        allowedASNs,
        blockedASNs: normalized,
      });
    };

    return (
      <Card title="网络归属控制配置" size="small">
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          {isSystemAdmin && (
            <Alert
              message="说明"
              description="仅当配置了允许或排除列表时本规则才会生效。可输入网络标识（如 15169）或机构名称关键字（如 Google）。"
              type="info"
              showIcon
            />
          )}

          <Card type="inner" size="small" title="允许的网络标识 (白名单)">
            <Select
              mode="tags"
              placeholder="输入或选择允许的网络代码或运营商名称"
              style={{ width: '100%' }}
              value={allowedASNs}
              onChange={handleAllowedChange}
              options={networkSignaturePresets}
              disabled={readonly}
              allowClear
            />
            {isSystemAdmin && (
              <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
                支持输入数字标识或关键词，命中白名单的网络将始终放行。
              </Text>
            )}
          </Card>

          <Card type="inner" size="small" title="排除的网络标识 (黑名单)">
            <Select
              mode="tags"
              placeholder="输入或选择需要拦截的网络代码或运营商名称"
              style={{ width: '100%' }}
              value={blockedASNs}
              onChange={handleBlockedChange}
              options={networkSignaturePresets}
              disabled={readonly}
              allowClear
            />
            {isSystemAdmin && (
              <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
                将频繁发送异常流量的网络放入黑名单以屏蔽其请求。
              </Text>
            )}
          </Card>
        </Space>
      </Card>
    );
  };

  // 爬虫拦截细粒度配置组件
  const CrawlerConfig = ({ config, setConfig, readonly = false }: { config: any; setConfig: (config: any) => void; readonly?: boolean }) => {
    // 使用Hook获取爬虫类型选项（从服务端获取）
    const { options: crawlerOptions, loading: crawlerLoading } = useBotTypeOptions();

    const modeKey = '__mode_crawler';
    const storedMode = config?.[modeKey];
    const hasBlocked = Array.isArray(config.crawlerBlocked) && config.crawlerBlocked.length > 0;
    const hasAllowed = Array.isArray(config.crawlerAllowed) && config.crawlerAllowed.length > 0;
    const defaultMode: 'allow' | 'block' = hasAllowed ? 'allow' : (hasBlocked ? 'block' : 'allow');
    const mode: 'allow' | 'block' = storedMode === 'allow' || storedMode === 'block' ? storedMode : defaultMode;

    const selectedCrawlers = useMemo<string[]>(() => {
      if (mode === 'allow') {
        return Array.isArray(config.crawlerAllowed) ? config.crawlerAllowed : [];
      }
      return Array.isArray(config.crawlerBlocked) ? config.crawlerBlocked : [];
    }, [mode, config.crawlerAllowed, config.crawlerBlocked]);

    const handleModeChange = (newMode: 'allow' | 'block') => {
      setConfig((prev: any) => ({
        ...prev,
        [modeKey]: newMode,
        crawlerAllowed: newMode === 'allow' ? selectedCrawlers : [],
        crawlerBlocked: newMode === 'block' ? selectedCrawlers : [],
      }));
    };

    const handleCrawlerChange = (crawlers: string[]) => {
      setConfig((prev: any) => ({
        ...prev,
        [modeKey]: mode,
        crawlerAllowed: mode === 'allow' ? crawlers : [],
        crawlerBlocked: mode === 'block' ? crawlers : [],
      }));
    };

    const getCrawlerName = (crawlerValue: string) => {
      const option = crawlerOptions.find(opt => opt.value === crawlerValue);
      return option?.label || crawlerValue;
    };

    // 如果正在加载，显示加载状态
    if (crawlerLoading) {
      return (
        <Card title="爬虫拦截配置" size="small">
          <Spin size="small" />
        </Card>
      );
    }

    return (
      <Card title="爬虫拦截配置" size="small">
        <Space direction="vertical" style={{ width: '100%' }}>
          {isSystemAdmin && (
            <Alert
              message="爬虫拦截配置说明"
              description="选择「允许」模式时，只有匹配允许列表中的爬虫才能访问；选择「阻止」模式时，匹配阻止列表中的爬虫将被拦截"
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}

          <div>
            <Space align="center" style={{ marginBottom: 8 }}>
              <Text strong>拦截模式：</Text>
              <ToggleSwitch value={mode} onChange={handleModeChange} disabled={readonly} />
            </Space>

            <Card size="small" style={{ marginTop: 16 }}>
              <Space align="center" style={{ marginBottom: 8 }}>
                <Text strong>{mode === 'allow' ? '允许' : '阻止'}的爬虫类型：</Text>
              </Space>
              <Select
                mode="multiple"
                style={{ width: '100%' }}
                placeholder={`选择${mode === 'allow' ? '允许' : '阻止'}的爬虫类型`}
                value={selectedCrawlers}
                onChange={handleCrawlerChange}
                disabled={readonly}
                showSearch
              >
                {crawlerOptions.map(option => (
                  <Option key={option.value} value={option.value}>
                    {option.label}
                  </Option>
                ))}
              </Select>
              {selectedCrawlers.length > 0 && (
                <div style={{ marginTop: 8 }}>
                  {selectedCrawlers.map(value => {
                    return (
                      <Tag 
                        key={value} 
                        color={mode === 'allow' ? 'green' : 'red'} 
                        style={{ marginRight: 8, marginBottom: 8 }}
                      >
                        {getCrawlerName(value)}
                      </Tag>
                    );
                  })}
                </div>
              )}
            </Card>
          </div>
        </Space>
      </Card>
    );
  };

  const renderConfigComponent = () => {
    if (!rule) return null;

    const componentProps = { config, setConfig, readonly };
    const key = `${rule.type}-${rule.name || 'new'}`;
    
    console.log('🔍 renderConfigComponent - key:', key, 'rule:', rule);

    switch (rule.type) {
      case "ip_blacklist":
        return <IPBlacklistConfig key={key} {...componentProps} />;
      case "url_referer":
        return <VisitorSourceConfig key={key} {...componentProps} />;
      case "geography":
        return <GeographyConfig key={key} {...componentProps} />;
      case "network":
        return <NetworkConfig key={key} {...componentProps} />;
      case "device":
        return <DeviceConfig key={key} {...componentProps} />;
      case "language":
        return <LanguageConfig key={key} {...componentProps} />;
      case "asn":
        return <ASNConfig key={key} {...componentProps} />;
      case "utm":
        return <UTMConfig key={key} {...componentProps} />;
      case "advertising":
        return <AdvertisingConfig key={key} {...componentProps} />;
      case "crawler":
        return <CrawlerConfig key={key} {...componentProps} />;
      default:
        return (
          <div>
            <Text type="secondary">该规则类型暂不支持详细配置</Text>
          </div>
        );
    }
  };
  return (
    <Modal
      title={readonly ? `查看规则 - ${rule?.name}` : `配置规则 - ${rule?.name}`}
      open={visible}
      onOk={readonly ? undefined : handleOk}
      onCancel={handleCancel}
      width={800}
      confirmLoading={saving}
      okText={readonly ? undefined : "保存配置"}
      cancelText="取消"
    >
      {renderConfigComponent()}
    </Modal>
  );
};

export default RuleEditModal;

